import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Play, MessageCircle, Mic, Volume2, VolumeX } from "lucide-react";
import { useState, useRef } from "react";
import analytics from "../../../../libs/analytics";

const Hero = () => {
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleMute = () => {
    if (iframeRef.current) {
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      // Send message to YouTube player
      iframeRef.current.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: newMutedState ? 'mute' : 'unMute'
        }),
        '*'
      );
    }
  };

  return (
    <section className="hero-gradient min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-vibrant/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/10 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border/50 mb-8"
          >
            <Mic className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">Ecossistema que destrava seu Inglês</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6"
          >
            Você até entende Inglês...{" "}
            <span className="text-gradient glow-text">mas trava na hora de falar?</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-foreground max-w-3xl mx-auto mb-30"
          >
            O Ataric é um ecossistema de prática de Inglês estruturada por voz, imersão interativa e desenvolvimento de habilidades reais — para você falar com segurança em situações reais.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <Button type="button" variant="cta" size="xl" className="w-full sm:w-auto" onClick={(e) => { analytics.landingCtaClicked('hero'); scrollToSection(e, 'planos'); }}>
              <MessageCircle className="w-5 h-5" />
              👉 Quero destravar meu inglês
            </Button>
            <Button type="button" variant="ctaOutline" size="xl" className="w-full sm:w-auto" onClick={(e) => { analytics.landingVideoClicked(); scrollToSection(e, 'video'); }}>
              <Play className="w-5 h-5" />
              Ver como funciona
            </Button>
          </motion.div>

          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            id="video"
            className="relative max-w-4xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden glow-purple">
              <div className="aspect-video bg-card border border-border rounded-2xl overflow-hidden relative">
                {/* YouTube Video Embed - Autoplay Muted */}
                <iframe
                  ref={iframeRef}
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/R2O4HajcWvY?autoplay=1&mute=1&loop=1&playlist=R2O4HajcWvY&controls=0&showinfo=0&rel=0&modestbranding=1&enablejsapi=1"
                  title="Vídeo de vendas Ataric"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
                
                {/* Mute/Unmute Button */}
                <motion.button
                  onClick={toggleMute}
                  className="absolute bottom-4 right-4 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 backdrop-blur-sm border border-white/20 text-white hover:bg-black/80 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  {isMuted ? (
                    <>
                      <VolumeX className="w-5 h-5" />
                      <span className="text-sm font-medium">Ativar som</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-5 h-5" />
                      <span className="text-sm font-medium">Som ativado</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
            
            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 md:-left-12 top-1/4 hidden md:block"
            >
              <div className="card-glass rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Conversação real</p>
                    <p className="text-xs text-muted-foreground">Fale por voz com a IA</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -right-4 md:-right-12 bottom-1/4 hidden md:block"
            >
              <div className="card-glass rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Feedback instantâneo</p>
                    <p className="text-xs text-muted-foreground">Correções em tempo real</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
